// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@werner/XDL/Utilities";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    title = "title",
    // XNG-Changes: add link & open in new tab
    link = "link",
    openInNewTab = "openInNewTab",
    // description = "description",
    // placeholder = "placeholder",
    // disclaimer = "disclaimer",
    // alignment = "alignment",
    // customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        // [fields.description]: string;
        // [fields.placeholder]: string;
        // [fields.disclaimer]: string;
        // [fields.alignment]: string;
        // [fields.customCSS]: string;
    };
}
// XNG-Changes: Remove Action
// const mapDispatchToProps = {
//     subscribe,
// };

type Props = OwnProps;

export interface SubscribeStyles {
    mainGridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
    disclaimerGridItem?: GridItemProps;
    disclaimerText?: TypographyPresentationProps;
    // XNG-Changes:
    wrapper?: InjectableCss;
    text?: TypographyPresentationProps;
    buttonStyles?: ButtonPresentationProps;
}

export const subscribeStyles: SubscribeStyles = {
    wrapper: {
        css: css`
            max-width: 220px;
            width: 100%;
            display: flex;
            flex-direction: column;
            row-gap: 8px;
            ${xScreen("lte", "sm")} {
                max-width: 100%;
            }
        `,
    },
    text: {
        size: 16,
        lineHeight: "22px",
        weight: "bold",
    },
    buttonStyles: {
        variant: "primary",
        css: css`
            height: 50px;
            text-align: left;
        `,
        typographyProps: {
            size: 16,
            lineHeight: "18px",
            weight: "normal",
        },
    },
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;

export const CmsSubscribe = ({ fields: { link, title, openInNewTab, label } }: Props) => {
    // const emailRequiredFieldMessage = siteMessage("EmailSubscription_EmailIsRequiredErrorMessage");
    // const emailFieldMessage = siteMessage("EmailSubscription_EmailIsInvalidErrorMessage");

    // const [email, setEmail] = useState("");
    // const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    // const [isSubmitted, setIsSubmitted] = useState(false);
    // const toasterContext = useContext(ToasterContext);

    const styles = useMergeStyles("subscribe", subscribeStyles);

    // const onSubscribeClick = () => {
    //     setIsSubmitted(true);
    //     if (!checkEmail(email)) {
    //         return;
    //     }

    //     subscribe({
    //         email,
    //         onSuccess: () => {
    //             toasterContext.addToast({ body: siteMessage("Email_Subscribe_Success"), messageType: "success" });
    //         },
    //         onComplete(resultProps) {
    //             if (resultProps.apiResult) {
    //                 // "this" is targeting the object being created, not the parent SFC
    //                 this.onSuccess?.();
    //             }
    //         },
    //     });
    // };

    // const emailChangeHandler = (email: string) => {
    //     checkEmail(email);
    //     setEmail(email);
    // };

    // const checkEmail = (email: string) => {
    //     const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;
    //     setEmailError(errorMessage);
    //     return !errorMessage;
    // };

    // const gridItemCss = (gridItemProps?: GridItemProps) => {
    //     return css`
    //         justify-content: ${fields.alignment ? fields.alignment : "center"};
    //         ${gridItemProps?.css}
    //     `;
    // };

    // const customCssWrapper = {
    //     css: css`
    //         ${fields.customCSS}
    //     `,
    // };

    //  XNG-Changes: Add new functionality
    const subscribeLink = useGetLink(link);

    if (!subscribeLink) {
        return null;
    }

    const subscribeClickHandler = () => {
        if (!openInNewTab) {
            window.location.href = `${subscribeLink.url}`;
        }

        window.open(subscribeLink.url);
    };
    // XNG-Changes: Change Structure
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="subscribe-widget">
            <Typography {...styles.text}>{title}</Typography>
            {subscribeLink && (
                <Button {...styles.buttonStyles} onClick={subscribeClickHandler} aria-label={`${label}`}>
                    {label}
                </Button>
            )}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

// XNG-Changes: Update fields and remove connect
const widgetModule: WidgetModule = {
    component: CmsSubscribe,
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            // {
            //     name: fields.alignment,
            //     displayName: "Layout",
            //     editorTemplate: "DropDownField",
            //     options: [
            //         { displayName: "Left", value: "flex-start" },
            //         { displayName: "Center", value: "center" },
            //         { displayName: "Right", value: "flex-end" },
            //     ],
            //     defaultValue: "center",
            //     fieldType: "Translatable",
            //     tab: basicTab,
            // },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Get the latest Werner news",
                fieldType: "Translatable",
                tab: basicTab,
            },
            // {
            //     name: fields.description,
            //     displayName: "Description",
            //     editorTemplate: "RichTextField",
            //     defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
            //     fieldType: "Translatable",
            //     tab: basicTab,
            // },
            // {
            //     name: fields.placeholder,
            //     displayName: "Placeholder Text",
            //     editorTemplate: "TextField",
            //     defaultValue: "Enter email address",
            //     fieldType: "Translatable",
            //     tab: basicTab,
            // },
            // {
            //     name: fields.disclaimer,
            //     displayName: "Disclaimer",
            //     editorTemplate: "RichTextField",
            //     defaultValue: "",
            //     fieldType: "Translatable",
            //     tab: basicTab,
            // },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                tab: basicTab,
            },
            // {
            //     name: fields.customCSS,
            //     displayName: "Custom CSS",
            //     editorTemplate: "CodeField",
            //     fieldType: "General",
            //     tab: advancedTab,
            //     defaultValue: defaultCustomCss,
            //     isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            //     validate: value => {
            //         if (value === undefined || value === null) {
            //             return "";
            //         }

            //         const result = cssLinter.parse(value, { silent: true });
            //         if (result?.stylesheet?.parsingErrors) {
            //             // the error output at this time only has room for one line so we just show the first error
            //             return result.stylesheet.parsingErrors.length <= 0
            //                 ? ""
            //                 : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
            //         }

            //         return "Unable to parse Css";
            //     },
            //     options: {
            //         mode: "css",
            //         lint: true,
            //         autoRefresh: true,
            //     },
            // },
            {
                fieldType: "General",
                name: fields.link,
                displayName: "Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.openInNewTab,
                displayName: "Open In New Tab",
                editorTemplate: "CheckboxField",
                variant: "toggle",
                defaultValue: false,
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
